@if (multiple) {
  <mat-form-field [appearance]="appearance" class="coverage-city-field full-width" subscriptSizing="dynamic">
    <mat-label>{{ label }}@if (required) { *}</mat-label>
    @if (loading) {
      <mat-spinner matSuffix diameter="20"></mat-spinner>
    } @else {
      <mat-icon matSuffix>location_city</mat-icon>
    }
    <mat-chip-grid #chipGrid aria-label="Selected coverage cities">
      @for (city of selectedCities; track city.id ?? city.name) {
        <mat-chip-row (removed)="removeCity(city)">
          {{ cityOptionLabel(city) }}
          <button matChipRemove type="button" [attr.aria-label]="'Remove ' + cityOptionLabel(city)">
            <mat-icon>cancel</mat-icon>
          </button>
        </mat-chip-row>
      }
      <input
        #chipSearchInput
        #cityAutocompleteTrigger="matAutocompleteTrigger"
        [placeholder]="selectedCities.length ? 'Search…' : placeholder"
        [formControl]="searchControl"
        [matChipInputFor]="chipGrid"
        [matAutocomplete]="cityAuto"
      />
    </mat-chip-grid>
    <mat-autocomplete
      #cityAuto="matAutocomplete"
      [class]="autocompletePanelClass"
      (opened)="onAutocompleteOpened()"
      (closed)="onAutocompleteClosed()"
      (optionSelected)="onCitySelected($event)">
      @if (!stationCoverageOnly && !hasSearchQuery) {
        <mat-option disabled class="coverage-city-prompt-option">{{ searchPrompt }}</mat-option>
      } @else if (loading) {
        <mat-option disabled>
          <span class="option-loading"><mat-spinner diameter="18"></mat-spinner> {{ loadingOptionsMessage }}</span>
        </mat-option>
      } @else if (loadError) {
        <mat-option disabled>{{ loadError }}</mat-option>
      } @else if (stationCoverageOnly && catalogLoaded && availableCities.length === 0) {
        <mat-option disabled>No cities with stations are available yet</mat-option>
      } @else if (selectableCities.length === 0) {
        <mat-option disabled>{{ emptyOptionsMessage }}</mat-option>
      } @else {
        @for (city of selectableCities; track city.id ?? city.name) {
          <mat-option [value]="cityOptionLabel(city)">{{ cityOptionLabel(city) }}</mat-option>
        }
      }
    </mat-autocomplete>
    @if (hint) {
      <mat-hint class="coverage-city-hint">{{ hint }}</mat-hint>
    } @else if (stationCoverageOnly) {
      <mat-hint class="coverage-city-hint">Select from the list or type to search</mat-hint>
    } @else {
      <mat-hint class="coverage-city-hint">{{ searchPrompt }}</mat-hint>
    }
    @if (required && selectedCities.length === 0 && searchControl.touched) {
      <mat-error>At least one coverage city is required</mat-error>
    }
  </mat-form-field>

  @if (allowAddCity) {
    <ng-container *ngTemplateOutlet="addCitySection"></ng-container>
  }
} @else {
  <mat-form-field [appearance]="appearance" class="coverage-city-field full-width" subscriptSizing="dynamic">
    <mat-label>{{ label }}@if (required) { *}</mat-label>
    <input
      #singleSearchInput
      #cityAutocompleteTrigger="matAutocompleteTrigger"
      matInput
      type="text"
      [placeholder]="placeholder"
      [formControl]="searchControl"
      [matAutocomplete]="singleCityAuto"
      (blur)="onBlur()"
    />
    @if (loading) {
      <mat-spinner matSuffix diameter="20"></mat-spinner>
    } @else {
      <mat-icon matSuffix>location_city</mat-icon>
    }
    <mat-autocomplete
      #singleCityAuto="matAutocomplete"
      [class]="autocompletePanelClass"
      (opened)="onAutocompleteOpened()"
      (closed)="onAutocompleteClosed()"
      (optionSelected)="onCitySelected($event)">
      @if (!hasSearchQuery) {
        <mat-option disabled class="coverage-city-prompt-option">{{ searchPrompt }}</mat-option>
      } @else if (loading) {
        <mat-option disabled>
          <span class="option-loading"><mat-spinner diameter="18"></mat-spinner> {{ loadingOptionsMessage }}</span>
        </mat-option>
      } @else if (loadError) {
        <mat-option disabled>{{ loadError }}</mat-option>
      } @else if (filteredCities.length === 0) {
        <mat-option disabled>{{ emptyOptionsMessage }}</mat-option>
      } @else {
        @for (city of filteredCities; track city.id ?? city.name) {
          <mat-option [value]="cityOptionLabel(city)">{{ cityOptionLabel(city) }}</mat-option>
        }
      }
    </mat-autocomplete>
    @if (hint) {
      <mat-hint class="coverage-city-hint">{{ hint }}</mat-hint>
    } @else {
      <mat-hint class="coverage-city-hint">{{ searchPrompt }}</mat-hint>
    }
    @if (required && !selectedCity && searchControl.touched) {
      <mat-error>Coverage city is required</mat-error>
    }
  </mat-form-field>

  @if (allowAddCity) {
    <ng-container *ngTemplateOutlet="addCitySection"></ng-container>
  }
}

<ng-template #addCitySection>
  <div class="add-city-section">
    @if (!showAddCityForm) {
      <div class="add-city-header">
        <span class="add-city-prompt">Can't find your city?</span>
        <button type="button" class="add-city-link" (click)="toggleAddCityForm()">
          <mat-icon>add</mat-icon>
          Add City
        </button>
      </div>
    } @else {
      <div class="add-city-form">
        <div class="add-city-fields-row">
          <div class="add-city-field-group">
            <label class="add-city-native-label" for="newCityName">City Name</label>
            <input
              id="newCityName"
              class="add-city-native-input"
              type="text"
              [formControl]="newCityNameControl"
              placeholder="e.g., Elmira"
            />
          </div>

          <div class="add-city-field-group">
            <label class="add-city-native-label" for="stateSearch">State</label>
            <input
              id="stateSearch"
              #stateSearchInput
              #stateAutocompleteTrigger="matAutocompleteTrigger"
              class="add-city-native-input"
              type="text"
              placeholder="Search & Select State"
              [formControl]="stateSearchControl"
              [matAutocomplete]="stateAuto"
            />
            <mat-autocomplete
              #stateAuto="matAutocomplete"
              [class]="autocompletePanelClass"
              (opened)="onStateAutocompleteOpened()"
              (closed)="onStateAutocompleteClosed()"
              (optionSelected)="onStateSelected($event)">
              @if (statesLoading) {
                <mat-option disabled>
                  <span class="option-loading"><mat-spinner diameter="18"></mat-spinner> Loading states…</span>
                </mat-option>
              } @else if (statesLoadError) {
                <mat-option disabled>{{ statesLoadError }}</mat-option>
              } @else if (filteredStates.length === 0) {
                <mat-option disabled>No states found</mat-option>
              } @else {
                @for (state of filteredStates; track state.stateCode) {
                  <mat-option [value]="stateOptionLabel(state)">{{ stateOptionLabel(state) }}</mat-option>
                }
              }
            </mat-autocomplete>
          </div>
        </div>

        @if (addCityError) {
          <p class="add-city-error">{{ addCityError }}</p>
        }

        <div class="add-city-actions">
          <button type="button" mat-button (click)="cancelAddCity()" [disabled]="creatingCity">Cancel</button>
          <button type="button" mat-flat-button color="primary" (click)="submitNewCity()" [disabled]="creatingCity">
            @if (creatingCity) {
              <mat-spinner diameter="18"></mat-spinner>
            } @else {
              Add City
            }
          </button>
        </div>
      </div>
    }
  </div>
</ng-template>
